import { request } from './client';
import type {
  AuthChallenge,
  AuthResult,
  AuthTokens,
  ChallengeAnswer,
  CodeDelivery,
  MeResponse,
  RegisterPayload,
  MfaPreference,
  RegisterResponse,
  TotpEnrolment,
  User,
} from './types';

/** /auth/*: public sign-in, sign-up and reset flows plus the signed-in helpers. */
export const authApi = {
  login: (email: string, password: string) =>
    request<AuthResult>('/auth/login', {
      method: 'POST',
      body: { email, password },
      auth: false,
    }),

  /** Starts SMS one-time-password sign-in; finish with challenge('SMS_OTP'). */
  loginWithPhone: (phone: string) =>
    request<AuthChallenge>('/auth/login/phone', { method: 'POST', body: { phone }, auth: false }),

  challenge: (answer: ChallengeAnswer) =>
    request<AuthResult>('/auth/challenge', { method: 'POST', body: answer, auth: false }),

  refresh: (username: string, refreshToken: string) =>
    request<AuthTokens>('/auth/refresh', {
      method: 'POST',
      body: { username, refreshToken },
      auth: false,
    }),

  logout: (refreshToken: string) =>
    request<void>('/auth/logout', { method: 'POST', body: { refreshToken }, auth: false }),

  /** Sign-up step 1. When `confirmation.required`, the code the provider sent goes to confirmSignUp(). */
  register: (payload: RegisterPayload) =>
    request<RegisterResponse>('/auth/register', { method: 'POST', body: payload, auth: false }),

  /** Sign-up step 2: the code the provider emailed (or texted). Not issued by the current provider. */
  confirmSignUp: (email: string, code: string) =>
    request<void>('/auth/confirm', { method: 'POST', body: { email, code }, auth: false }),

  /** A fresh sign-up code; `destination` is null when nothing was sent. */
  resendConfirmation: (email: string) =>
    request<CodeDelivery>('/auth/confirm/resend', {
      method: 'POST',
      body: { email },
      auth: false,
    }),

  forgotPassword: (email: string) =>
    request<void>('/auth/forgot-password', { method: 'POST', body: { email }, auth: false }),

  resetPassword: (email: string, code: string, newPassword: string) =>
    request<void>('/auth/reset-password', {
      method: 'POST',
      body: { email, code, newPassword },
      auth: false,
    }),

  me: () => request<MeResponse>('/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),

  /** Sets (optional) and sends a verification code to the phone number. */
  sendPhoneCode: (phone?: string) =>
    request<{ destination: string | null }>('/auth/phone/send-code', {
      method: 'POST',
      body: phone ? { phone } : {},
    }),

  verifyPhone: (code: string) =>
    request<User>('/auth/phone/verify', { method: 'POST', body: { code } }),

  /** TOTP enrolment, step 1: the secret for the authenticator app. */
  startTotp: () => request<TotpEnrolment>('/auth/mfa/totp/start', { method: 'POST' }),

  /** TOTP enrolment, step 2: the app's first code; returns the user with isMfaEnabled refreshed. */
  confirmTotp: (code: string) =>
    request<User>('/auth/mfa/totp/confirm', { method: 'POST', body: { code } }),

  /** Which second factor to use, or NONE to turn it off. */
  setMfaPreference: (method: MfaPreference) =>
    request<User>('/auth/mfa/preference', { method: 'PUT', body: { method } }),

  /** Promotional email on or off for the signed-in account. */
  setMarketingPreference: (optOut: boolean) =>
    request<User>('/auth/marketing', { method: 'PUT', body: { optOut } }),
};
